import { Fragment } from 'react';
import PropTypes from 'prop-types';
import ImageUploading from 'react-images-uploading';
import styled from 'styled-components';

import { Button } from '@mui/material';
import Grid from 'material/components/Grid';
import { imageType } from 'common/typings';

const FixedWidthButton = styled(Button)`
  width: 105px;

  &:first-of-type {
    margin-bottom: 15px;
  }
`;

const LogoButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 15px;
`;

const StyledImg = styled.img`
  padding-right: 15px;
`;

const CHOOSE_LOGO_MESSAGE = 'Choose Logo';
const THUMBNAIL_WIDTH = '120';
const SAVE_IMAGES_TEXT = 'Save Image';

/**
 * Controlled single-image picker built on `react-images-uploading`: shows the
 * current image thumbnail with "Choose Logo" / "Save Image" buttons. Use for
 * logo/image uploads in branding settings and the action center and signup
 * creation flows, holding the picked image list in parent state.
 */
const ImageUploader = ({
  defaultImageScr,
  disabled,
  images,
  onSave,
  setImages,
  showSaveButton,
}) => {
  const onChange = imageList => {
    setImages(imageList);
  };

  return (
    <ImageUploading dataURLKey="data_url" onChange={onChange} singleImage value={images}>
      {({ imageList, onImageUpload, onImageUpdate }) => (
        <Grid container justifyContent="flex-end">
          {defaultImageScr && !imageList.length && (
            <StyledImg alt="Logo" src={defaultImageScr} width={THUMBNAIL_WIDTH} />
          )}
          {imageList.map((image, index) => (
            <Fragment key={image.data_url}>
              <img alt="Logo" src={image.data_url} width={THUMBNAIL_WIDTH} />
              <LogoButtonsWrapper>
                <FixedWidthButton
                  color="secondary"
                  disableElevation
                  disabled={disabled}
                  onClick={() => onImageUpdate(index)}
                  variant="contained"
                >
                  {CHOOSE_LOGO_MESSAGE}
                </FixedWidthButton>
                {showSaveButton && (
                  <FixedWidthButton
                    color="secondary"
                    disableElevation
                    disabled={disabled}
                    onClick={onSave}
                    variant="contained"
                  >
                    {SAVE_IMAGES_TEXT}
                  </FixedWidthButton>
                )}
              </LogoButtonsWrapper>
            </Fragment>
          ))}
          {imageList.length === 0 && (
            <Button
              color="secondary"
              disableElevation
              disabled={disabled}
              onClick={onImageUpload}
              variant="contained"
            >
              {CHOOSE_LOGO_MESSAGE}
            </Button>
          )}
        </Grid>
      )}
    </ImageUploading>
  );
};

ImageUploader.defaultProps = {
  defaultImageScr: null,
  disabled: false,
  onSave: () => {},
  showSaveButton: true,
};

ImageUploader.propTypes = {
  /** URL of the already-saved image, shown until a new image is picked (sic: "Scr"). */
  defaultImageScr: PropTypes.string,
  disabled: PropTypes.bool,
  images: PropTypes.arrayOf(imageType).isRequired,
  /** Called when "Save Image" is clicked; pair with `showSaveButton`. */
  onSave: PropTypes.func,
  setImages: PropTypes.func.isRequired,
  /** Hide the "Save Image" button when saving happens via the parent form instead. */
  showSaveButton: PropTypes.bool,
};

export default ImageUploader;
